import {
  Box,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { PatientBasicData, TaskState } from "../features/data/patientApiSlice";

import ClinicianBadgeGroup from "./ClinicianBadgeGroup";
import ConcernPillList from "./ConcernPillList";
import FlaggedForSupervisor from "../../assets/supervisor_review.svg?react";
import OpenIcon from "@mui/icons-material/ArrowForwardIos";
import { SelectionSource } from "../features/api/logging";
import { createStyleSheet } from "../utils/styles";
import { getDisplayDateFromBasicTask } from "./TaskListItem";
import { useCallback } from "react";

const MAX_BADGES = 3;

const PatientListItem = (props: {
  id?: string;
  patient: PatientBasicData;
  onClick: (patientUid: string, selectionSource: SelectionSource) => void;
  selected: boolean;
}) => {
  const { id, patient, onClick, selected } = props;
  const theme = useTheme();

  const handleClick = useCallback(() => {
    onClick(patient.uid, SelectionSource.TaskList);
  }, [onClick, patient.uid]);

  return (
    <ListItemButton
      id={id}
      onClick={handleClick}
      sx={{
        borderRadius: selected ? 2 : 0,
        border: (theme) =>
          "1px solid " +
          (selected ? alpha(theme.palette.primary.main, 0.3) : "transparent"),
        borderBottom: (theme) =>
          !selected ? `1px solid ${alpha(theme.palette.grey[500], 0.3)}` : "",
        backgroundColor: (theme) =>
          selected ? alpha(theme.palette.primary.main, 0.1) : "",
        position: "relative",
        [theme.breakpoints.down("desktop")]: {
          padding: 0.5,
        },
      }}
    >
      <ListItemText
        primary={
          <Box sx={styles.titleContainer}>
            <Typography
              variant="h6"
              noWrap
              color={
                patient.tasks.some((task) => task.state === TaskState.todo)
                  ? "primary"
                  : "textSecondary"
              }
            >
              {patient.name}
            </Typography>
            {patient.pending_hcw_message ? (
              <Box sx={styles.newMessageDot} />
            ) : null}
            <ConcernPillList
              tasks={patient.tasks}
              overlap
              singlePillHasText
              maxPills={5}
              sx={{ marginLeft: 1 }}
            />
          </Box>
        }
        secondary={getDisplayDateFromBasicTask(patient.tasks[0])}
        slotProps={{
          secondary: { noWrap: true, sx: styles.secondaryText },
        }}
      />
      <ClinicianBadgeGroup
        clinicians={patient.clinicians_working_in_record || []}
        maxBadges={MAX_BADGES}
      />
      <OpenIcon
        sx={
          [
            styles.openIcon,
            selected ? styles.iconSelected : styles.icon,
          ] as SxProps<Theme>
        }
        fontSize="small"
      />
      {patient.tasks.some((basicTask) => basicTask.flagged_for_supervisor) ? (
        <Box sx={styles.flaggedForSupervisor}>
          <FlaggedForSupervisor fill={theme.palette.flagForSupervisor.main} />
        </Box>
      ) : null}
    </ListItemButton>
  );
};

const styles = createStyleSheet({
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  secondaryText: {
    display: "inline",
  },
  newMessageDot: {
    backgroundColor: (theme) => theme.palette.newMessageDot.main,
    marginLeft: 1,
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
  openIcon: {
    marginLeft: 1,
  },
  icon: {
    color: (theme) => theme.palette.text.secondary,
  },
  iconSelected: {
    color: (theme) => theme.palette.primary.main,
  },
  flaggedForSupervisor: {
    position: "absolute",
    top: 0,
    right: 0,
    pointerEvents: "none",
  },
});

export default PatientListItem;
